import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { canonicaliseGroupName, isIndependentGroup } from '../../lib/groupCanonical';
import type { Account } from '../../types/account';
import { getAccountDetail, listAccounts } from './api';
import {
  importLocations,
  type ImportGroupDecision,
  type ImportResult,
} from './importApi';
import {
  parseXlsxFileRaw,
  toImportRow,
  type ParseResult,
  type RawParseResult,
} from './xlsxParse';
import {
  applyMappingAndBulkSet,
  invertColumnMapping,
} from './applyMappingAndBulkSet';
import {
  initialMappingFor,
  MapColumnsStep,
  validateMandatoryMappings,
  type BulkSetState,
  type ColumnMappingState,
} from './MapColumnsStep';

// Round-6: the bulk upload flow now runs as a four-step state
// machine:
//
//   1. 'file'    — AM picks the .xlsx file.
//   2. 'mapping' — Map XLS columns to system fields (Table 1) +
//                  bulk-set enum defaults (Table 2).
//   3. 'preview' — Group decisions + row preview, same as the
//                  legacy flow.
//   4. 'done'    — Import result. Same as legacy flow.
//
// Pre-round-6 the file-pick step jumped straight to preview using
// the legacy COLUMN_ALIASES auto-match. The mapping step is now the
// explicit interstitial — the AM confirms or corrects what the auto-
// match suggested before the preview renders.

type Step = 'file' | 'mapping' | 'preview' | 'done';

type GroupChoice = {
  canonicalKey: string;
  sourceSpellings: string[];
  rowCount: number;
  decision: { kind: 'reuse'; accountId: string } | { kind: 'create'; name: string } | { kind: 'independent' };
};

function bucketRows(parseResult: ParseResult): Map<string, GroupChoice> {
  const buckets = new Map<string, GroupChoice>();
  for (const r of parseResult.rows) {
    const key = canonicaliseGroupName(r.groupRaw);
    const existing = buckets.get(key);
    if (existing) {
      existing.rowCount++;
      if (!existing.sourceSpellings.includes(r.groupRaw)) {
        existing.sourceSpellings.push(r.groupRaw);
      }
    } else {
      buckets.set(key, {
        canonicalKey: key,
        sourceSpellings: [r.groupRaw],
        rowCount: 1,
        decision: isIndependentGroup(key)
          ? { kind: 'independent' }
          : { kind: 'create', name: r.groupRaw.trim() || '(unnamed)' },
      });
    }
  }
  return buckets;
}

export function ImportLocations() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: ['account', id],
    queryFn: () => getAccountDetail(id!),
    enabled: !!id,
  });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });

  // State machine + per-step buckets.
  const [step, setStep] = useState<Step>('file');
  const [raw, setRaw] = useState<RawParseResult | null>(null);
  const [mapping, setMapping] = useState<ColumnMappingState>({});
  const [bulkSet, setBulkSet] = useState<BulkSetState>({});
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [groupChoices, setGroupChoices] = useState<Map<string, GroupChoice>>(new Map());
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const existingGroupsUnderBrand = useMemo<Account[]>(() => {
    if (!accounts.data || !id) return [];
    return accounts.data.filter(
      (a) => a.parentAccountId === id && a.accountType === 'group',
    );
  }, [accounts.data, id]);

  const importMut = useMutation({
    mutationFn: async () => {
      if (!id || !parseResult) throw new Error('No parsed rows.');
      const groups: ImportGroupDecision[] = [...groupChoices.values()].map((c) => {
        const d = c.decision;
        if (d.kind === 'independent') {
          return { canonicalKey: c.canonicalKey, name: '(independent)' };
        }
        if (d.kind === 'reuse') {
          const existing = existingGroupsUnderBrand.find((a) => a.id === d.accountId);
          return {
            canonicalKey: c.canonicalKey,
            name: existing?.name ?? c.sourceSpellings[0] ?? '',
            existingAccountId: d.accountId,
          };
        }
        return { canonicalKey: c.canonicalKey, name: d.name };
      });
      const rows = parseResult.rows.map(toImportRow);
      return importLocations(id, { groups, rows });
    },
    onSuccess: async (res) => {
      setResult(res);
      setStep('done');
      await qc.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  if (detail.isLoading || accounts.isLoading) return <CenteredSpinner />;
  if (!detail.data || detail.data.account.accountType !== 'brand') {
    return (
      <div className="text-red-600 text-sm">
        Import is only available for brand-type accounts.{' '}
        <Link to="/accounts" className="underline">Back to accounts</Link>
      </div>
    );
  }

  const brand = detail.data.account;

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    setError(null);
    setResult(null);
    setParseResult(null);
    setGroupChoices(new Map());
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      // Round-6: raw read mode — no auto-aliasing. The mapping
      // step seeds dropdowns via initialMappingFor and the AM
      // confirms / corrects before preview.
      const r = await parseXlsxFileRaw(file);
      setRaw(r);
      setMapping(initialMappingFor(r.headers));
      setBulkSet({});
      setStep('mapping');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  function continueFromMapping() {
    if (!raw) return;
    const result = applyMappingAndBulkSet(
      raw,
      invertColumnMapping(mapping),
      bulkSet,
    );
    setParseResult(result);
    setGroupChoices(bucketRows(result));
    setStep('preview');
  }

  function backToMapping() {
    setStep('mapping');
  }

  function updateChoice(key: string, decision: GroupChoice['decision']) {
    setGroupChoices((prev) => {
      const next = new Map(prev);
      const existing = next.get(key);
      if (!existing) return prev;
      next.set(key, { ...existing, decision });
      return next;
    });
  }

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl font-semibold">Import locations into {brand.name}</h1>
        <p className="text-sm text-slate-600">
          Uploads an xlsx of locations. Map columns + set bulk defaults, preview groupings, then confirm to create accounts and franchise location profiles.
        </p>
      </div>

      <section className="bg-white rounded-md border border-slate-200 p-6 space-y-3">
        <label className="block text-sm font-medium text-slate-700">XLSX file</label>
        <input type="file" accept=".xlsx,.xls" onChange={onFile} />
        {raw && (
          <p className="text-sm text-slate-600">
            Parsed {raw.rows.length} row{raw.rows.length === 1 ? '' : 's'} ·{' '}
            {raw.headers.length} column{raw.headers.length === 1 ? '' : 's'}.
          </p>
        )}
      </section>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">{error}</div>
      )}

      {step === 'mapping' && raw && (() => {
        // Round-6 commit 6: gate the Continue button until every
        // mandatory field has been mapped from XLS in Table 1 (or
        // bulk-set in Table 2 — reserved for fields that can be
        // satisfied by either source). The validator returns the
        // friendly labels of any unsatisfied fields so the message
        // below the tables reads naturally.
        const validation = validateMandatoryMappings(mapping, bulkSet);
        return (
          <>
            <MapColumnsStep
              headers={raw.headers}
              mapping={mapping}
              onMappingChange={setMapping}
              bulkSet={bulkSet}
              onBulkSetChange={setBulkSet}
            />
            {!validation.ok && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                Map all mandatory fields to continue:{' '}
                <strong>{validation.missing.join(', ')}</strong>.
              </div>
            )}
            <div className="flex items-center gap-3">
              <Button
                onClick={continueFromMapping}
                disabled={!validation.ok}
                title={
                  validation.ok
                    ? undefined
                    : `Missing: ${validation.missing.join(', ')}`
                }
              >
                Continue to preview
              </Button>
              <Link to={`/accounts/${id}`}>
                <Button variant="outline">Cancel</Button>
              </Link>
            </div>
          </>
        );
      })()}

      {step === 'preview' && parseResult && (
        <>
          {parseResult.warnings.length > 0 && (
            <ul className="text-sm text-amber-700 list-disc pl-5">
              {parseResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}

          <section className="bg-white rounded-md border border-slate-200 p-6 space-y-4">
            <h2 className="text-lg font-semibold">Group mapping ({groupChoices.size} distinct)</h2>
            <p className="text-xs text-slate-500">
              One row per canonicalised group name. Source spellings from the sheet are shown underneath. Choose whether to reuse an existing group account, create a new one, or treat rows as independent (placed directly under the brand).
            </p>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-4 font-medium">Canonical</th>
                  <th className="py-2 pr-4 font-medium">Source spellings</th>
                  <th className="py-2 pr-4 font-medium">Rows</th>
                  <th className="py-2 pr-4 font-medium">Decision</th>
                </tr>
              </thead>
              <tbody>
                {[...groupChoices.values()].map((c) => (
                  <tr key={c.canonicalKey} className="border-t border-slate-100 align-top">
                    <td className="py-2 pr-4 font-mono text-xs">{c.canonicalKey || '(empty)'}</td>
                    <td className="py-2 pr-4">
                      <ul className="list-disc pl-5">
                        {c.sourceSpellings.map((s, i) => <li key={i}>{s || '(blank)'}</li>)}
                      </ul>
                    </td>
                    <td className="py-2 pr-4">{c.rowCount}</td>
                    <td className="py-2 pr-4 space-y-2">
                      <label className="block text-sm">
                        <input
                          type="radio"
                          name={`d-${c.canonicalKey}`}
                          checked={c.decision.kind === 'independent'}
                          onChange={() => updateChoice(c.canonicalKey, { kind: 'independent' })}
                        />{' '}
                        Independent (directly under brand)
                      </label>
                      <label className="block text-sm">
                        <input
                          type="radio"
                          name={`d-${c.canonicalKey}`}
                          checked={c.decision.kind === 'create'}
                          onChange={() =>
                            updateChoice(c.canonicalKey, {
                              kind: 'create',
                              name: c.sourceSpellings[0] ?? c.canonicalKey,
                            })
                          }
                        />{' '}
                        Create new group named{' '}
                        <input
                          type="text"
                          className="border border-slate-300 rounded px-2 py-0.5 text-sm ml-1 w-64"
                          value={c.decision.kind === 'create' ? c.decision.name : ''}
                          onChange={(e) =>
                            updateChoice(c.canonicalKey, { kind: 'create', name: e.target.value })
                          }
                          disabled={c.decision.kind !== 'create'}
                        />
                      </label>
                      <label className="block text-sm">
                        <input
                          type="radio"
                          name={`d-${c.canonicalKey}`}
                          checked={c.decision.kind === 'reuse'}
                          onChange={() => {
                            const first = existingGroupsUnderBrand[0];
                            if (!first) return;
                            updateChoice(c.canonicalKey, { kind: 'reuse', accountId: first.id });
                          }}
                          disabled={existingGroupsUnderBrand.length === 0}
                        />{' '}
                        Reuse existing group:{' '}
                        <select
                          className="border border-slate-300 rounded px-2 py-0.5 text-sm ml-1"
                          value={c.decision.kind === 'reuse' ? c.decision.accountId : ''}
                          onChange={(e) => updateChoice(c.canonicalKey, { kind: 'reuse', accountId: e.target.value })}
                          disabled={c.decision.kind !== 'reuse'}
                        >
                          {existingGroupsUnderBrand.map((a) => (
                            <option key={a.id} value={a.id}>{a.name}</option>
                          ))}
                        </select>
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="bg-white rounded-md border border-slate-200 p-6">
            <h2 className="text-lg font-semibold mb-3">Rows ({parseResult.rows.length})</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="py-1 pr-3">Suburb</th>
                    <th className="py-1 pr-3">State</th>
                    <th className="py-1 pr-3">Group</th>
                    <th className="py-1 pr-3">Meta $</th>
                    <th className="py-1 pr-3">Google $</th>
                    <th className="py-1 pr-3">FB Ad Acct</th>
                    <th className="py-1 pr-3">GA Customer</th>
                    <th className="py-1 pr-3">Offer</th>
                  </tr>
                </thead>
                <tbody>
                  {parseResult.rows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="py-1 pr-3">{r.franchiseSuburb}</td>
                      <td className="py-1 pr-3">{r.state || <span className="text-amber-700">(missing)</span>}</td>
                      <td className="py-1 pr-3">{r.groupRaw || <span className="text-slate-400">—</span>}</td>
                      <td className="py-1 pr-3">{r.metaMonthlyBudget !== null ? `$${(r.metaMonthlyBudget / 100).toFixed(0)}` : '—'}</td>
                      <td className="py-1 pr-3">{r.googleMonthlyBudget !== null ? `$${(r.googleMonthlyBudget / 100).toFixed(0)}` : '—'}</td>
                      <td className="py-1 pr-3 font-mono text-[11px]">{r.fbAdAccountId ?? <span className="text-slate-400">—</span>}</td>
                      <td className="py-1 pr-3 font-mono text-[11px]">{r.googleAdsCustomerId ?? <span className="text-slate-400">—</span>}</td>
                      <td className="py-1 pr-3 truncate max-w-[240px]">{r.offerLandingPage || <span className="text-amber-700">(missing)</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <div className="flex items-center gap-3">
            <Button onClick={() => importMut.mutate()} disabled={importMut.isPending}>
              {importMut.isPending ? 'Importing…' : `Import ${parseResult.rows.length} locations`}
            </Button>
            <Button variant="outline" onClick={backToMapping} disabled={importMut.isPending}>
              Back to mapping
            </Button>
            <Link to={`/accounts/${id}`}>
              <Button variant="outline">Cancel</Button>
            </Link>
          </div>
        </>
      )}

      {step === 'done' && result && (
        <section className="bg-emerald-50 border border-emerald-200 rounded-md p-6 space-y-3">
          <h2 className="text-lg font-semibold text-emerald-900">Import complete</h2>
          <p className="text-sm text-emerald-900">
            Groups created: {result.groupsCreated.length}; reused: {result.groupsReused.length}.
            Locations created: {result.locationsCreated.length}; skipped: {result.locationsSkipped.length}.
            Errors: {result.errors.length}.
          </p>
          {result.locationsSkipped.length > 0 && (
            <details className="text-sm">
              <summary>Skipped rows</summary>
              <ul className="list-disc pl-5">
                {result.locationsSkipped.map((s, i) => <li key={i}>{s.franchiseSuburb}: {s.reason}</li>)}
              </ul>
            </details>
          )}
          {result.errors.length > 0 && (
            <details className="text-sm text-red-800">
              <summary>Errors</summary>
              <ul className="list-disc pl-5">
                {result.errors.map((e, i) => <li key={i}>{e.franchiseSuburb}: {e.error}</li>)}
              </ul>
            </details>
          )}
          <Link to={`/accounts/${id}`} className="text-sm underline">Back to brand</Link>
        </section>
      )}
    </div>
  );
}
